import { yupResolver } from '@hookform/resolvers/yup';
import {
  Divider,
  FormHelperText,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { FormStepComponentProps } from 'components/Forms/MultiStepForm';
import { useMaterials } from 'hooks';
import { CharacterizationForm } from 'types';
import { formatKg } from 'utils/weight';
import { CharacterizationFormContext } from 'pages/Characterizations/CharacterizationCreate';
import { transformId } from 'services/apiPlatformTransformer';
import { characterizationFormSchema } from './Step2.yup';
import TableSeparatorRow from '../../TableSeparatorRow';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  previousStepInfo: {
    width: '100%',
    margin: '0 0 12px 0',
    backgroundColor: '#E9EDEF',
    display: 'flex',
    listStyleType: 'none',
    justifyContent: 'center',
    padding: '1.3em 0',
    '& li': {
      margin: '0 4em',
    },
  },
  bold: {
    fontWeight: 600,
    margin: 0,

    '&:after': {
      content: '" :"',
    },
  },
  fieldLine: {
    marginTop: 8,

    '& > div': {
      minWidth: 470,
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: 214,
    },
  },
  borderNone: {
    border: 'none',
  },
  materialTable: {
    width: 470,
    paddingTop: 12,
  },
  materialWeightCell: {
    width: '50%',
    padding: '1px 13px',
  },
  materialNameCell: {
    padding: '6.5px 22px',
  },
  materialLabel: {
    fontSize: 'inherit',
    color: 'inherit',
  },
  subMaterialRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  subMaterialNameCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    borderBottom: 'none',
    padding: '6.5px 22px',
    fontSize: '12px',
    color: '#737F8F',
    weight: 400,
  },
  subMaterialWeightCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    borderBottom: 'none',
    padding: '1px 13px',
  },
  subMaterialWeightInput: {
    width: '100%',
    '& input': {
      fontSize: '12px',
      weight: 400,
      color: '#737F8F',
      textAlign: 'right',
    },
  },
  totalWeightCell: {
    padding: '7.5px 13px',
  },
  totalWeightField: {
    width: 171,
    textAlign: 'right',
    padding: 0,
  },
  headerCell: {
    padding: '0 22px',
    borderBottom: 0,
  },
  headerCellSubMaterials: {
    width: '100%',
    padding: '0 22px',
    borderBottom: 0,
  },
}));

interface TotalWeightProps {
  materialsToWatch: string[] | undefined;
  control: any;
}

const TotalWeight: React.FC<TotalWeightProps> = ({ control, materialsToWatch }) => {
  const watchMaterialWeights = useWatch({ control, name: materialsToWatch ?? [] });
  const totalWeight = watchMaterialWeights.reduce(
    (total: number, newWeight: string | null | undefined) => total + parseFloat(newWeight || '0'),
    0,
  );
  return (
    <NumberFormat
      decimalSeparator=","
      decimalScale={3}
      isNumericString
      suffix=" kg"
      fixedDecimalScale={true}
      displayType="text"
      value={totalWeight}
    />
  );
};

type MaterialWeightPath = `characterizationMaterials.${number}.weight`;

const Step2: React.FC<FormStepComponentProps<CharacterizationForm, CharacterizationFormContext>> = ({
  onSubmit,
  formName,
  previousStepData,
  formContext,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'characterizationForm' });

  const schema = useMemo(() => characterizationFormSchema(t), [t]);

  const {
    control,
    handleSubmit,
    register,
    trigger,
    setValue,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm<CharacterizationForm>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: previousStepData,
    resolver: yupResolver(schema),
  });

  const materials = useMaterials(
    previousStepData.stream
      ? {
          'streams.id': previousStepData.stream,
        }
      : undefined,
  );
  const materialWeightIds = useMemo(
    () => materials?.map((_, index): MaterialWeightPath => `characterizationMaterials.${index}.weight`) ?? [],
    [materials],
  );

  const onKeyPressCharacterizationMaterialWeight = (index: number) => {
    if (!materials) return;
    materials[index].subMaterials?.forEach((material, indexSubMaterial) => {
      if (getValues(`characterizationMaterials.${index}.characterizationSubMaterials.${indexSubMaterial}.weight`)) {
        setValue(`characterizationMaterials.${index}.characterizationSubMaterials.${indexSubMaterial}.weight`, '');
      }
    });
  };

  const onKeyPressCharacterizationSubMaterialWeight = (index: number) => {
    const characterizationSubMaterials = getValues(`characterizationMaterials.${index}.characterizationSubMaterials`);
    const totalWeights =
      characterizationSubMaterials?.reduce((total, characSubMat) => {
        const currentWeight = parseFloat(characSubMat.weight);
        return total + (!isNaN(currentWeight) ? currentWeight : 0);
      }, 0) ?? 0;

    if (0 === totalWeights) return;

    setValue(`characterizationMaterials.${index}.weight`, totalWeights);
  };

  return (
    <>
      <ul className={classes.previousStepInfo}>
        {formContext?.client?.shortName && (
          <li>
            <p className={classes.bold}>{t('client')}</p>
            <span>{formContext?.client?.shortName}</span>
          </li>
        )}
        {formContext?.stream?.name && (
          <li>
            <p className={classes.bold}>{t('stream')}</p>
            <span>{formContext?.stream?.name}</span>
          </li>
        )}
        <li>
          <p className={classes.bold}>{t('samplingDateDescriptive')}</p>
          <span>{previousStepData?.samplingDate?.toLocaleDateString()}</span>
        </li>
        {previousStepData?.analyseDate && (
          <li>
            <p className={classes.bold}>{t('analyseDateDescriptive')}</p>
            <span>{previousStepData?.analyseDate?.toLocaleDateString()}</span>
          </li>
        )}
        <li>
          <p className={classes.bold}>{t('weight')}</p>
          <span>{formatKg(previousStepData?.weight ?? null)}</span>
        </li>
      </ul>
      <Divider variant="middle" />
      <form id={formName} onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        {materials && (
          <TableContainer className={classes.materialTable}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>{t('material')}</TableCell>
                  <TableCell className={classes.headerCellSubMaterials}>{t('subMaterial')}</TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    {t('weightShort')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materials?.map((material, index) => (
                  <>
                    <TableRow key={`characterizationMaterials.${material['@id']}`}>
                      <TableCell className={classes.materialNameCell}>
                        <FormLabel className={classes.materialLabel} htmlFor={`characterizationMaterials.${index}`}>
                          {material.shortName}
                        </FormLabel>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell className={classes.materialWeightCell}>
                        <Controller
                          name={`characterizationMaterials.${index}.weight`}
                          control={control}
                          render={({ field: { ref, ...rest }, fieldState }) => (
                            <WeightInputField
                              id={`characterizationMaterials.${index}`}
                              unit="kg"
                              defaultValue={''}
                              inputRef={ref}
                              helperText={fieldState.error?.message}
                              disabled={
                                (formContext?.isEditing &&
                                  previousStepData.characterizationMaterials &&
                                  (previousStepData.characterizationMaterials[index] as any)
                                    ?.characterizationSubMaterials?.length > 0) ||
                                (!formContext?.isEditing && material.subMaterials && material.subMaterials.length > 0)
                              }
                              error={fieldState.invalid}
                              {...rest}
                              onBlur={() =>
                                isSubmitted &&
                                trigger([`characterizationMaterials.${index}.weight`, 'characterizationMaterials'])
                              }
                              onKeyPress={() => onKeyPressCharacterizationMaterialWeight(index)}
                            />
                          )}
                        />
                        <input
                          hidden
                          {...register(`characterizationMaterials.${index}.material`, {
                            shouldUnregister: true,
                            value: material['@id'],
                          })}
                        />
                      </TableCell>
                    </TableRow>
                    {/* @ts-ignore */}
                    {errors?.characterizationMaterials?.[index]?.characterizationSubMaterials?.message && (
                      // @ts-ignore
                      <FormHelperText error>
                        {/* @ts-ignore */}
                        {errors?.characterizationMaterials?.[index]?.characterizationSubMaterials?.message}
                      </FormHelperText>
                    )}
                    {material.subMaterials
                      ?.filter(subMaterial => {
                        if (!formContext?.isEditing) return true;
                        // @ts-ignore
                        return previousStepData.characterizationMaterials?.[index]?.characterizationSubMaterials?.find(
                          (characSubMaterial: { weight: string; subMaterial: string; sortingCenter: string }) =>
                            transformId(characSubMaterial.subMaterial) === subMaterial.id.toString(),
                        );
                      })
                      ?.sort((a, b) => a.name.localeCompare(b.name))
                      .map((subMaterial, indexSubMaterial) => (
                        <TableRow key={`characterizationSubMaterials.${indexSubMaterial}`}>
                          <TableCell className={classes.borderNone}></TableCell>
                          <TableCell className={classes.subMaterialNameCell}>
                            <FormLabel
                              className={classes.materialLabel}
                              htmlFor={`characterizationMaterials.${index}.characterizationSubMaterials.${indexSubMaterial}`}
                            >
                              {subMaterial.name}
                            </FormLabel>
                          </TableCell>
                          <TableCell className={classes.subMaterialWeightCell}>
                            <Controller
                              // ATTENTION à l'ORDER BY: le name se réfère à l'ordre des SubMaterials et le weight à l'ordre des CharacterizationSubMaterials
                              name={`characterizationMaterials.${index}.characterizationSubMaterials.${indexSubMaterial}.weight`}
                              control={control}
                              render={({ field: { ref, ...rest }, fieldState }) => (
                                <WeightInputField
                                  id={`characterizationMaterials.${index}.characterizationSubMaterials.${indexSubMaterial}`}
                                  unit="kg"
                                  defaultValue={''}
                                  inputRef={ref}
                                  className={classes.subMaterialWeightInput}
                                  helperText={fieldState.error?.message}
                                  error={fieldState.invalid}
                                  onKeyUp={() => onKeyPressCharacterizationSubMaterialWeight(index)}
                                  {...rest}
                                />
                              )}
                            />
                            <input
                              hidden
                              {...register(
                                `characterizationMaterials.${index}.characterizationSubMaterials.${indexSubMaterial}.subMaterial`,
                                {
                                  shouldUnregister: true,
                                  value: subMaterial['@id'],
                                },
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </>
                ))}
                <TableSeparatorRow>Total</TableSeparatorRow>
                <TableRow>
                  <TableCell className={classes.totalWeightCell} />
                  <TableCell className={classes.totalWeightCell} />
                  <TableCell className={classes.totalWeightCell}>
                    <div className={classes.totalWeightField}>
                      <TotalWeight control={control} materialsToWatch={materialWeightIds} />
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {/* @ts-ignore */}
            {errors?.characterizationMaterials?.message && (
              // @ts-ignore
              <FormHelperText error>{errors?.characterizationMaterials?.message}</FormHelperText>
            )}
          </TableContainer>
        )}
      </form>
    </>
  );
};

export default Step2;
